import type {
  BalancePosting,
  BalancePostingWrapper,
} from '@festina/life-general-types/life'
import {
  amountUtils,
  dateUtils,
  stringUtils,
  translationUtils,
} from '@festina-life/core'
import { CircularSpinner } from '@festina-life/ui/feedback'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import { withSuspense } from '@festina-life/ui/libs'
import {
  createColumnDefiner,
  type RowAction,
  SortableTable,
} from '@festina-life/ui/listsAndTables'
import { Card, CardBody, CardHeader } from '@festina-life/ui/structure'
import { Suspense, useMemo, useState } from 'react'

import { useLazyFetchLifeBalancePostingsForWorkPadIds } from '../../hooks/lifePostings/useLazyFetchLifeBalancePostingsForWorkPadIds'
import { useAmountFormatter } from '../../hooks/useAmountFormatter'
import { withLifeErrorBoundary } from '../../libs/withLifeErrorBoundary'
import { POSTING_DIMENSIONS_AND_KEYS } from '../../utility/postingUtils'
import type { BalancePostingWrapperWithSum } from '../balancePostTable/types'
import { ErrorMessage } from '../errorFallback/ErrorMessage'
import { PostingVoucherModal } from '../postings/PostingVoucherModal'
import { hasBalancePostingVoucherRef } from './helpers'

type Props = {
  workPadIds: string[]
}

const defineColumn = createColumnDefiner<BalancePostingWrapperWithSum>()

const EventBalancePostingsTableInternal = ({ workPadIds }: Props) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const { formatWithDecimals } = useAmountFormatter()
  const [activePosting, setActivePosting] = useState<BalancePosting>()
  const [filteredTableData, setFilteredTableData] =
    useState<BalancePostingWrapper[]>()
  const { postingWrappers, isLoading, error } =
    useLazyFetchLifeBalancePostingsForWorkPadIds(workPadIds)

  const satisfiedPostingWrappers = postingWrappers satisfies
    | BalancePostingWrapperWithSum[]
    | undefined

  const columns = useMemo(
    () => ({
      accountType: defineColumn({
        heading: translate('postings.column.accountType'),
        keyPath: 'balancePosting.accountType',
        filterMode: 'auto',
      }),
      amount: defineColumn({
        heading: translate('postings.column.amount'),
        keyPath: 'balancePosting.amount',
        formatter: formatWithDecimals,
        alignment: 'right',
      }),
      postingType: defineColumn({
        heading: translate('postings.column.postingType'),
        keyPath: 'balancePosting.postingType',
        formatter: (type) => translateEnum('life.enum.PostingType', type),
        filterMode: 'auto',
      }),
      seqno: defineColumn({
        heading: translate('postings.column.seqno'),
        keyPath: 'balancePosting.seqno',
        sortOptions: { sortType: 'asc' },
      }),
      transactionTime: defineColumn({
        heading: translate('postings.column.transactionTime'),
        keyPath: 'balancePosting.transactionTime',
        formatter: dateUtils.dateAsStr,
        sortValue: (date) => date,
        filterMode: 'dateRange',
      }),
      truncated: defineColumn({
        heading: translate('postings.column.truncated'),
        keyPath: 'balancePosting.truncated',
        formatter: (x) => translationUtils.translateBoolean(x, translate),
      }),
      valueDate: defineColumn({
        heading: translate('postings.column.valueDate'),
        keyPath: 'balancePosting.valueDate',
        formatter: dateUtils.dateAsStr,
        sortValue: (date) => date,
      }),
      ...Object.fromEntries(
        POSTING_DIMENSIONS_AND_KEYS.map((dim) => [
          dim,
          defineColumn({
            heading: translateEnum('enum.PostingDimensions', dim),
            keyPath: `balancePosting.dimensions.${dim}`,
            filterMode: 'auto',
          }),
        ]),
      ),
    }),
    [formatWithDecimals, translate, translateEnum],
  )

  const rowActions = (row: BalancePostingWrapperWithSum): RowAction[] => [
    {
      key: 'view',
      iconName: 'DocumentText' as const,
      title: translate('postings.column.voucher'),
      // In some cases it seems the voucher may be missing.
      // Attempting to open the voucher modal with no voucher would fail.
      onClick: hasBalancePostingVoucherRef(row)
        ? () => {
            setActivePosting(row.balancePosting)
          }
        : undefined,
    },
  ]

  const postingFooter: BalancePostingWrapperWithSum[] = useMemo(() => {
    const postingWrappersData = filteredTableData ?? postingWrappers

    return [
      {
        _type: 'BalancePostingWrapper',
        balancePosting: {
          _type: 'sumRow',
          accountType: translate('total'),
          amount:
            amountUtils.sumWithRounding(
              postingWrappersData?.map(
                (wrapper) => wrapper.balancePosting?.amount,
              ),
            ) ?? 0,
        },
      },
    ]
  }, [filteredTableData, postingWrappers, translate])

  const handleFilteredTableData = (data: BalancePostingWrapper[]) => {
    // We need to compare the data to the filteredTableData to avoid an infinite render loop. This should be cleaned (Jira: LIFE-4435)
    if (
      stringUtils.hash(JSON.stringify(filteredTableData ?? [])) !==
      stringUtils.hash(JSON.stringify(data ?? []))
    ) {
      setFilteredTableData(data)
    }
  }

  return satisfiedPostingWrappers?.length ? (
    <>
      {activePosting && activePosting._id && (
        <Suspense>
          <PostingVoucherModal
            postingId={activePosting._id}
            sequenceNumber={activePosting.seqno}
            onClose={() => setActivePosting(undefined)}
            vouchersPostingType="BalancePosting"
          />
        </Suspense>
      )}
      <Card>
        <CardHeader title={translate('eventBalancePostingsTable.title')} />
        <CardBody>
          <SortableTable
            data={satisfiedPostingWrappers}
            footerData={postingFooter}
            onPresentDataChange={handleFilteredTableData}
            columns={columns}
            rowActions={rowActions}
          />
        </CardBody>
      </Card>
    </>
  ) : (
    <>
      {error && <ErrorMessage error={error} />}
      {isLoading && <CircularSpinner />}
    </>
  )
}

export const EventBalancePostingsTable = withLifeErrorBoundary(
  withSuspense(EventBalancePostingsTableInternal),
)
